export type NavigationCell = { row: number; col: number };

export const buildNavigationField = (
  width: number,
  height: number,
  targets: NavigationCell[],
  isOpen: (row: number, col: number) => boolean,
): Int16Array => {
  const field = new Int16Array(width * height);
  field.fill(-1);
  const queue: number[] = [];
  for (const target of targets) {
    if (!isOpen(target.row, target.col)) continue;
    const index = target.row * width + target.col;
    if (field[index] === 0) continue;
    field[index] = 0;
    queue.push(index);
  }
  let head = 0;
  while (head < queue.length) {
    const index = queue[head++];
    const row = Math.floor(index / width);
    const col = index % width;
    const nextDistance = field[index] + 1;
    for (const [rowDelta, colDelta] of [[-1, 0], [0, 1], [1, 0], [0, -1]] as const) {
      const nextRow = row + rowDelta;
      const nextCol = col + colDelta;
      if (nextRow < 0 || nextRow >= height || nextCol < 0 || nextCol >= width || !isOpen(nextRow, nextCol)) continue;
      const nextIndex = nextRow * width + nextCol;
      if (field[nextIndex] >= 0) continue;
      field[nextIndex] = nextDistance;
      queue.push(nextIndex);
    }
  }
  return field;
};

export const getNavigationStep = (
  width: number,
  height: number,
  field: Int16Array,
  from: NavigationCell,
  bias: number,
): NavigationCell | undefined => {
  const directions = bias > 0
    ? [[-1, 0], [0, 1], [1, 0], [0, -1]] as const
    : [[-1, 0], [0, -1], [1, 0], [0, 1]] as const;
  let best = { ...from, distance: field[from.row * width + from.col] };
  if (best.distance === 0) return undefined;
  for (const [rowDelta, colDelta] of directions) {
    const row = from.row + rowDelta;
    const col = from.col + colDelta;
    if (row < 0 || row >= height || col < 0 || col >= width) continue;
    const distance = field[row * width + col];
    if (distance >= 0 && (best.distance < 0 || distance < best.distance)) best = { row, col, distance };
  }
  if (best.distance < 0) return undefined;
  return { row: best.row, col: best.col };
};
